import { Injectable } from '@angular/core';
import {
  BackgroundOperationColorProfile,
  BackgroundOperationResult,
  BackgroundOperationState,
} from '../../state-stores/background-operation.state';

const DEFAULT_COLOR_PROFILE: BackgroundOperationColorProfile = 'green';
const DEFAULT_POLL_INTERVAL_MS = 10000;
const DEFAULT_PENDING_MESSAGE = 'Working...';
const DEFAULT_PENDING_SUB_MESSAGE = "This may take a while. You'll be notified when done.";

export interface BackgroundOperationOptions {
  /** Unique key for this kind of operation (e.g. 'batch-billing-create'). */
  id: string;
  /** Visual theme for this operation's toast and result modal. Defaults to 'green'. */
  colorProfile?: BackgroundOperationColorProfile;
  /** Title shown in the non-blocking corner toast while this operation is pending. */
  pendingMessage?: string;
  /** Secondary line shown in the corner toast. Defaults to a generic "this may take a while" message. */
  pendingSubMessage?: string;
  /** Kicks off the operation. Should resolve once the request has been accepted (fire-and-forget). */
  run: () => Promise<void>;
  /** Called on an interval; return true once the operation has finished. Thrown/rejected polls are ignored and retried. */
  poll: () => Promise<boolean>;
  /** How often to call poll(), in milliseconds. Defaults to 10000 (10s). */
  pollIntervalMs?: number;
  /** Builds the result shown once poll() resolves true. */
  buildResult: () => Promise<BackgroundOperationResult> | BackgroundOperationResult;
  /** Builds the result shown if run() rejects. Defaults to a generic error result. */
  buildErrorResult?: (error: any) => BackgroundOperationResult;
}

/**
 * Orchestrates long-running "fire and forget" background operations (batch
 * generation, bulk deletes, report generation, etc.), driving
 * BackgroundOperationState so progress/results stay visible even if the user
 * navigates to a different page while the operation is still running.
 *
 * Usage: call `start()` with a `run` function that kicks off the work and a
 * `poll` function that reports when it's done.
 */
@Injectable({ providedIn: 'root' })
export class BackgroundOperationRunner {
  private readonly intervals = new Map<string, ReturnType<typeof setInterval>>();

  constructor(private readonly state: BackgroundOperationState) {}

  start(options: BackgroundOperationOptions): void {
    const colorProfile = options.colorProfile ?? DEFAULT_COLOR_PROFILE;
    this.clearInterval(options.id);
    this.state.addPending({
      id: options.id,
      message: options.pendingMessage ?? DEFAULT_PENDING_MESSAGE,
      subMessage: options.pendingSubMessage ?? DEFAULT_PENDING_SUB_MESSAGE,
      colorProfile,
    });

    options.run().catch((error: any) => {
      this.finish(
        options.id,
        options.buildErrorResult
          ? options.buildErrorResult(error)
          : {
              operationId: options.id,
              title: 'Error',
              message:
                error?.message ||
                'An error occurred while processing your request.',
              isError: true,
            },
        colorProfile,
      );
    });

    const interval = setInterval(async () => {
      try {
        const done = await options.poll();
        if (done) {
          this.clearInterval(options.id);
          const result = await options.buildResult();
          this.finish(options.id, result, colorProfile);
        }
      } catch {
        // ignore transient poll errors — keep polling
      }
    }, options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS);

    this.intervals.set(options.id, interval);
  }

  dismissResult(): void {
    this.state.setResult(null);
  }

  private finish(
    id: string,
    result: BackgroundOperationResult,
    colorProfile: BackgroundOperationColorProfile,
  ): void {
    this.clearInterval(id);
    this.state.removePending(id);
    this.state.setResult({ ...result, colorProfile: result.colorProfile ?? colorProfile });
  }

  private clearInterval(id: string): void {
    const interval = this.intervals.get(id);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(id);
    }
  }
}
